/**
 * Notification API client.
 *
 * Read state is a user-interface convenience and nothing more. Marking a Revision Request read does
 * not satisfy it, and dismissing it does not dismiss the obligation — the workspace keeps showing
 * both, which is why nothing here touches planning routes.
 */

import type {
  NotificationCategory,
  NotificationKind,
  NotificationPreferences,
} from '@pius/contracts';

import { request } from '../../api/client';

export interface InboxEntry {
  id: string;
  kind: NotificationKind;
  category: NotificationCategory;
  liturgy_id: string | null;
  liturgy_title: string | null;
  payload: Record<string, unknown>;
  read: boolean;
  created_at: string;
}

export interface DeliveryRecord {
  id: string;
  kind: NotificationKind;
  /** Already masked by the server. The client never sees a whole address here. */
  to: string | null;
  language_code: string;
  state: string;
  failure_code: string | null;
  attempts: number;
  created_at: string;
  delivered_at: string | null;
  resendable: boolean;
}

export function fetchInbox(): Promise<{ data: InboxEntry[]; unread: number }> {
  return request('/notifications');
}

export function markRead(id: string): Promise<void> {
  return request(`/notifications/${id}/read`, { method: 'POST' });
}

export function markAllRead(): Promise<void> {
  return request('/notifications/read-all', { method: 'POST' });
}

export function dismiss(id: string): Promise<void> {
  return request(`/notifications/${id}/dismiss`, { method: 'POST' });
}

export function fetchPreferences(): Promise<{ data: NotificationPreferences }> {
  return request('/me/notification-preferences');
}

export function savePreferences(
  next: Partial<NotificationPreferences>,
): Promise<{ data: NotificationPreferences }> {
  return request('/me/notification-preferences', {
    method: 'PUT',
    body: JSON.stringify(next),
  });
}

export function fetchDeliveries(liturgyId: string): Promise<{ data: DeliveryRecord[] }> {
  return request(`/liturgies/${liturgyId}/notifications`);
}

export function resendDelivery(liturgyId: string, recipientId: string): Promise<{ id: string }> {
  return request(`/liturgies/${liturgyId}/notifications/${recipientId}/resend`, {
    method: 'POST',
  });
}

/**
 * Where a notification points.
 *
 * Built from the notification's own `liturgy_id` rather than from `payload.link_path`, which is a
 * value the server composed for an email. Deriving the in-app destination here keeps a payload
 * field from becoming a way to steer navigation.
 */
export function destinationOf(entry: InboxEntry): string | null {
  if (!entry.liturgy_id) return null;

  return entry.kind.startsWith('planning.')
    ? `/liturgies/${entry.liturgy_id}/planning`
    : `/liturgies/${entry.liturgy_id}`;
}

/**
 * What each kind is, in a sentence a person would say.
 *
 * Shared with the History screen, which lists delivery attempts by kind: an identifier like
 * `planning.revision_requested` is fine in a database and unreadable in a list somebody scans.
 */
